import React from 'react'

import { isEmpty } from 'lodash'
import { Layers as LayersIcon } from 'react-feather'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Box, Chip, Grid, Paper, Typography } from '@material-ui/core'

import {
  BarChart,
  DonutChart,
  Container,
  ContentHeader,
  LoadingFeedback,
  Page,
} from 'components'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'

import useFetch from 'hooks/useFetch'

import theme from 'theme'
import * as service from 'service'
import useStyles from './styles/indicators'

const RISK_LABELS = ['Baixo', 'Médio', 'Alto']
const RISK_KEYS = ['low', 'medium', 'high']

const STAT_CARDS = [
  {
    key: 'total',
    label: 'Total de IAs',
    color: theme.palette.pinkMagenta,
  },
  {
    key: 'highRisk',
    label: 'Com risco alto',
    color: theme.palette.redErrorDark,
  },
  {
    key: 'processesPersonalData',
    label: 'Processam dados pessoais',
    color: theme.palette.azure,
  },
]

const AiInventoryIndicators = () => {
  const classes = useStyles()

  const { response, isLoading } = useFetch(service.dponet.aiInventory.stats)

  const stats = response?.data?.stats || {}

  const byRisk = stats.byRisk || {}
  const byDepartment = stats.byDepartment || []
  const departmentsAdoption = stats.departmentsAdoption || {}
  const byRiskAndDepartment = stats.byRiskAndDepartment || []

  const statValues = {
    total: stats.total ?? 0,
    highRisk: byRisk.high ?? 0,
    processesPersonalData: stats.processesPersonalData ?? 0,
  }

  const riskValues = RISK_KEYS.map((key) => byRisk[key] ?? 0)

  const stackedDepartmentNames = byRiskAndDepartment.map((row) => row.name)
  const stackedDepartmentSeries = [
    {
      name: 'Baixo',
      data: byRiskAndDepartment.map((row) => (row.low || []).length),
    },
    {
      name: 'Médio',
      data: byRiskAndDepartment.map((row) => (row.medium || []).length),
    },
    {
      name: 'Alto',
      data: byRiskAndDepartment.map((row) => (row.high || []).length),
    },
  ]

  const donutValues = [
    departmentsAdoption.usingAi ?? 0,
    departmentsAdoption.notUsingAi ?? 0,
  ]
  const donutNames = ['Usam IA', 'Não usam IA']

  return (
    <Page title="Indicadores de Governança de IA">
      <LoadingFeedback open={isLoading} />
      <Container maxWidth={false}>
        <ContentHeader title="Indicadores" />

        {/* Row 1 — stat cards */}
        <Grid container spacing={3}>
          {STAT_CARDS.map((card) => (
            <Grid key={card.key} item xs={12} sm={4}>
              <Paper variant="outlined" className={classes.statCard}>
                <Box
                  className={classes.statCardIcon}
                  style={{ backgroundColor: card.color }}
                >
                  <LayersIcon size={28} />
                </Box>
                <Box className={classes.statCardContent}>
                  <Typography variant="h4" className={classes.statCardValue}>
                    {statValues[card.key]}
                  </Typography>
                  <Typography
                    variant="caption"
                    className={classes.statCardLabel}
                  >
                    {card.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Row 2 — bar charts */}
        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Box className={classes.chartHeader}>
                  <Typography
                    variant="subtitle1"
                    className={classes.chartTitle}
                  >
                    Quantidade de IA por risco
                  </Typography>
                  <Typography
                    variant="caption"
                    className={classes.chartSubtitle}
                  >
                    Risco baixo, médio e alto
                  </Typography>
                </Box>
                <BarChart
                  id="ai-by-risk"
                  names={RISK_LABELS}
                  series={[{ name: 'Quantidade', data: riskValues }]}
                  colors={[
                    theme.palette.success.main,
                    theme.palette.warning.main,
                    theme.palette.error.main,
                  ]}
                  distributed
                  hideLegend
                  heightValue={260}
                  borderRadius={4}
                  showLabels
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" className={classes.chartPaper}>
                <Box className={classes.chartHeader}>
                  <Typography
                    variant="subtitle1"
                    className={classes.chartTitle}
                  >
                    Quantidade de IA por local de ocorrência
                  </Typography>
                  <Typography
                    variant="caption"
                    className={classes.chartSubtitle}
                  >
                    Por departamento
                  </Typography>
                </Box>
                <BarChart
                  id="ai-by-department"
                  names={stackedDepartmentNames}
                  series={stackedDepartmentSeries}
                  colors={[
                    theme.palette.success.main,
                    theme.palette.warning.main,
                    theme.palette.error.main,
                  ]}
                  heightValue={260}
                  borderRadius={4}
                  roundAllBars
                  showLabels
                  enabledTotal
                  dataLabelFormatter={(val) => (val > 0 ? Math.round(val) : '')}
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Row 3 — table + donut */}
        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper>
                <Box className={classes.tableHeader}>
                  <Typography
                    variant="subtitle1"
                    className={classes.sectionTitle}
                  >
                    Risco de IA por departamento
                  </Typography>
                </Box>
                <PerfectScrollbar
                  options={{ wheelPropagation: false, useBothWheelAxes: true }}
                >
                  <Box width="100%">
                    <Table
                      size="small"
                      noWrap
                      emptyMessage="Nenhum dado disponível."
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell width="25%">Departamento</TableCell>
                          <TableCell width="25%">
                            <Chip
                              label="Risco Baixo"
                              className={classes.chipLow}
                              size="small"
                            />
                          </TableCell>
                          <TableCell width="25%">
                            <Chip
                              label="Risco Médio"
                              className={classes.chipMedium}
                              size="small"
                            />
                          </TableCell>
                          <TableCell width="25%">
                            <Chip
                              label="Risco Alto"
                              className={classes.chipHigh}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isEmpty(byRiskAndDepartment)
                          ? null
                          : byRiskAndDepartment.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell className={classes.departmentName}>
                                  {row.name}
                                </TableCell>
                                <TableCell>
                                  <ToolList tools={row.low} classes={classes} />
                                </TableCell>
                                <TableCell>
                                  <ToolList
                                    tools={row.medium}
                                    classes={classes}
                                  />
                                </TableCell>
                                <TableCell>
                                  <ToolList
                                    tools={row.high}
                                    classes={classes}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                      </TableBody>
                    </Table>
                  </Box>
                </PerfectScrollbar>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper variant="outlined" className={classes.sectionPaper}>
                <Typography
                  variant="subtitle1"
                  className={classes.sectionTitle}
                >
                  Departamentos que utilizam IA
                </Typography>
                <Typography variant="caption" className={classes.chartSubtitle}>
                  Usam IA, Não usam IA
                </Typography>
                <DonutChart
                  id="department-usage"
                  names={donutNames}
                  values={donutValues}
                  title="Departamentos"
                  heightValue={300}
                  colors={[
                    theme.palette.greenPantone,
                    theme.palette.pinkMagenta,
                  ]}
                  legendPosition="bottom"
                  fixedTitle
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Page>
  )
}

const ToolList = ({ tools, classes }) => {
  if (isEmpty(tools)) {
    return (
      <Typography variant="caption" className={classes.emptyCell}>
        —
      </Typography>
    )
  }

  return (
    <Box className={classes.toolList}>
      {tools.map((tool) => (
        <Box key={tool.name} className={classes.iconCell}>
          {tool.iconUrl ? (
            <img
              src={tool.iconUrl}
              alt={tool.name}
              className={classes.toolIcon}
            />
          ) : (
            <LayersIcon size={16} color={theme.palette.secondary.main} />
          )}
          <Typography variant="body2" className={classes.toolName}>
            {tool.name}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

export default AiInventoryIndicators
